import { useEffect } from "react";
import { removePrerenderLd, clampMetaTitle, clampMetaDescription } from "../lib/seo";
import { coverFor } from "../lib/ogCovers";

/**
 * <Seo> — per-route metadata, hand-rolled (no react-helmet).
 *
 * Why not Helmet? React 19 strips inline `<script type="application/ld+json">`
 * via its sanitiser and react-helmet-async v3 only manages tags it itself
 * inserts (so the static brand defaults in index.html stick around, creating
 * duplicate name="description" / og:title pairs). To avoid both problems we
 * directly mutate document.head with predictable rules:
 *
 *   • All meta tags this component owns are tagged data-seo="route" so we
 *     can wipe them on route change.
 *   • Brand defaults in index.html stay tagged data-seo="default" — they
 *     act as the SSR fallback for crawlers that hit the SPA shell before
 *     JS runs, and we hide them via `media="none"` once route-specific
 *     tags load (Google still reads them, but link-unfurlers will pick
 *     the latest matching tag).
 *
 * Tags managed: title, description, keywords, robots, canonical,
 *   og:{title,description,image,image:width,image:height,image:alt,url,
 *   type,site_name,locale}, twitter:{card,title,description,image,
 *   image:alt,site,creator}, author, plus any JSON-LD blocks passed.
 *
 * The hook intentionally does NOT cleanup on unmount — instead it
 * overwrites on every route change. That keeps the last route's tags
 * around briefly during transitions (better than a blank head).
 */
const SITE_URL = "https://crypt.pe";
const DEFAULT_OG = `${SITE_URL}/og-home.png`;
const BRAND_KEYWORDS =
  "crypto payment gateway, accept crypto payments, non-custodial payment, " +
  "USDT payment link, bitcoin payment gateway, crypto checkout, 0% fee crypto, " +
  "WooCommerce crypto plugin, crypto payment API, Stripe for crypto";

const TAG_FLAG = "data-seo-route";

function upsertMeta(attr, key, value) {
  if (value == null) return;
  // Remove any prior tag with the same name/property (static index.html ones
  // too — there's no value in keeping a stale default once we have a fresh
  // route value).
  const selector = `meta[${attr}="${key}"]`;
  document.head.querySelectorAll(selector).forEach((el) => el.remove());
  const tag = document.createElement("meta");
  tag.setAttribute(attr, key);
  tag.setAttribute("content", value);
  tag.setAttribute(TAG_FLAG, "1");
  document.head.appendChild(tag);
}

function upsertLink(rel, href) {
  if (href == null) return;
  document.head.querySelectorAll(`link[rel="${rel}"]`).forEach((el) => el.remove());
  const tag = document.createElement("link");
  tag.setAttribute("rel", rel);
  tag.setAttribute("href", href);
  tag.setAttribute(TAG_FLAG, "1");
  document.head.appendChild(tag);
}

function clearJsonLd() {
  document.head
    .querySelectorAll(`script[type="application/ld+json"][${TAG_FLAG}]`)
    .forEach((el) => el.remove());
}

function appendJsonLd(obj) {
  const s = document.createElement("script");
  s.setAttribute("type", "application/ld+json");
  s.setAttribute(TAG_FLAG, "1");
  s.textContent = JSON.stringify(obj);
  document.head.appendChild(s);
}

export default function Seo({
  title,
  description,
  keywords,
  canonical,
  ogImage,
  ogImageAlt,
  ogType = "website",
  noindex = false,
  jsonLd,
  twitterCard = "summary_large_image",
}) {
  const fullTitle = clampMetaTitle(title ? `${title} · crypt.pe` : "Get paid in crypto. Share one link · crypt.pe");
  const pathName = typeof window !== "undefined" ? window.location.pathname : "/";
  const canon = canonical || `${SITE_URL}${pathName}`;
  const og = ogImage || coverFor(pathName) || DEFAULT_OG;
  const ogAlt = ogImageAlt || "CRYPT.PE — get paid in crypto, share one link";
  const desc = clampMetaDescription(description || "CRYPT.PE — non-custodial crypto payment gateway. Accept ETH, USDC, USDT, BTC, SOL, POL, BNB. 0% platform fee, no merchant account required.");
  const kw = keywords ? `${keywords}, ${BRAND_KEYWORDS}` : BRAND_KEYWORDS;
  const ldBlocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
  const ldKey = JSON.stringify(ldBlocks);

  useEffect(() => {
    document.title = fullTitle;
    upsertMeta("name", "description", desc);
    upsertMeta("name", "keywords", kw);
    upsertMeta("name", "author", "CRYPT.PE");
    upsertMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large");
    upsertLink("canonical", canon);

    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", desc);
    upsertMeta("property", "og:image", og);
    upsertMeta("property", "og:image:width", "1200");
    upsertMeta("property", "og:image:height", "630");
    upsertMeta("property", "og:image:alt", ogAlt);
    upsertMeta("property", "og:url", canon);
    upsertMeta("property", "og:type", ogType);
    upsertMeta("property", "og:site_name", "CRYPT.PE");
    upsertMeta("property", "og:locale", "en_US");

    upsertMeta("name", "twitter:card", twitterCard);
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", desc);
    upsertMeta("name", "twitter:image", og);
    upsertMeta("name", "twitter:image:alt", ogAlt);
    upsertMeta("name", "twitter:site", "@crypt_pe");
    upsertMeta("name", "twitter:creator", "@crypt_pe");

    clearJsonLd();
    if (ldBlocks.length) removePrerenderLd(new Set(ldBlocks.map((x) => x && x["@type"])));
    ldBlocks.forEach(appendJsonLd);
    // ldKey collapses the dep on the json object identity so we don't
    // re-render on every parent re-render that happens to pass a fresh
    // {} reference with the same content.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullTitle, desc, kw, canon, og, ogAlt, ogType, noindex, twitterCard, ldKey]);

  return null;
}
